import React, { useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { CirclePlusIcon, DownloadIcon, ExternalLinkIcon, PencilIcon, TrashIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Chip } from "@/components/badge/chip";
import {
  allChannelStateKeys,
  channelFlowStart,
  ChannelStateChip,
  DEFAULT_CHANNEL_RELEASABLE_STATES,
  defaultChannelFlow,
} from "@/components/badge/channel-state";
import { StateBadge } from "@/components/badge/state-badge";
import { RowCard } from "@/components/list/row-card";
import { Property, PropertyList } from "@/components/detail/property";
import { RowIndex } from "@/components/list/row-index";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { Section } from "@/components/section";
import {
  DetailAside,
  DetailColumns,
  DetailMain,
} from "@/components/detail-columns";
import { useApp } from "@/hooks/apps";
import { useToast } from "@/hooks/use-toast";
import { withCurrentTime } from "@/lib/date";
import { toastError } from "@/lib/toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { delFetcher, fetcher, mutateRequest, postFetcher, swrKey, writeAtom } from "@/lib/swr";
import { formatSize } from "@/components/versions/artifacts-card";
import {
  ChannelFormDialog,
  ChannelRow,
  DeleteChannelButton,
  playStoreUrl,
} from "./app-channels";

interface ChannelDetail extends ChannelRow {
  releases: {
    id: number;
    versionId: string;
    releasedAt: string;
    /** Staged-rollout percent (1–100). */
    percent?: number | null;
    /** Channel-flow node this record stepped to (null on legacy rows). */
    state?: string | null;
    operatorName?: string;
    version?: {
      id: string;
      versionName?: string;
      versionCode?: string;
      state?: number;
    };
  }[];
}

type ChannelRelease = ChannelDetail["releases"][number];

interface VersionOption {
  id: string;
  versionName?: string;
  versionCode?: string;
  state?: number;
}


// One channel's full history: which system versions shipped this channel
// package and when (newest first). Channel properties (package, flavor
// mapping) live in the right rail; reference build-config version shown when
// the channel predates in-system tracking.
const AppChannelDetail = () => {
  const { id, channelId } = useParams();
  const navigate = useNavigate();
  const app = useApp(id);
  const { data: channel, isLoading } = useSWR<ChannelDetail>([
    `/channels/${channelId}`,
  ]);
  const { toast } = useToast();

  // Record a release from the channel side: pick the version here (unlike the
  // version page's Channels tab where the version is fixed). Date defaults to
  // today; staged rollouts append one log per stage.
  const [creating, setCreating] = useState(false);
  const [cVersionId, setCVersionId] = useState("");
  const [cDate, setCDate] = useState("");
  const [cPercent, setCPercent] = useState("");
  const [cState, setCState] = useState("");
  // 「Next」进来时限定只能选当前节点的 nexts;null = 记录发布,全节点可选
  const [cNextsOnly, setCNextsOnly] = useState<string[] | null>(null);
  const flowMap = app?.channelStateFlow ?? defaultChannelFlow();
  // 弹窗里可选的 flow 节点 = 该 App 渠道流转图启用的状态(目录序)
  const flowNodes = allChannelStateKeys().filter((s) => s in flowMap);
  // 渠道发版门槛:只列走到门槛状态的版本(默认待发版/审核中/放量中/线上,
  // App Settings → Flow 可按 App 配置;服务端写路径同口径校验)
  const releasableStates =
    app?.channelReleasableStates ?? DEFAULT_CHANNEL_RELEASABLE_STATES;
  // 打渠道包(Jenkins BuildOnce):选版本,flavor 固定为本渠道的 flavorPid
  const [buildOpen, setBuildOpen] = useState(false);
  const [bVersionId, setBVersionId] = useState("");
  const [bMode, setBMode] = useState<"apk" | "all">("all");
  const { data: vdata } = useSWR<{ data: VersionOption[] }>(
    creating || buildOpen
      ? swrKey("/versions", { pid: id, state: releasableStates, pageSize: 100 })
      : null,
    { keepPreviousData: true }
  );
  const { data: buildOptions } = useSWR<{
    supported: boolean;
    needFlavors: boolean;
  }>(buildOpen && bVersionId ? [`/versions/${bVersionId}/build/options`] : null);
  const { trigger: triggerBuild, isMutating: building } = useSWRMutation(
    bVersionId ? `/versions/${bVersionId}/build` : null,
    postFetcher,
    {
      onSuccess: () => {
        setBuildOpen(false);
        toast({ description: "打包已提交,Jenkins 排队中;产物回流后见版本 Artifacts。" });
      },
      onError: (error: unknown) => toastError(toast, "打包提交失败", error),
    }
  );
  const versionOptions = vdata?.data ?? [];
  // The POST echoes the saved record (no joined version): prepend it to the
  // newest-first log with the version picked in the form; the other channel
  // views revalidate in the background (WED-249).
  const { trigger: createRelease, isMutating: creatingRelease } =
    useSWRMutation("/channel-releases", postFetcher<ChannelRelease>, {
      onSuccess: (saved) => {
        writeAtom<ChannelDetail>(`/channels/${channelId}`, (cur) => ({
          ...cur,
          releases: [
            { ...saved, version: versionOptions.find((v) => v.id === saved.versionId) },
            ...cur.releases,
          ],
        }));
        mutateRequest("/channels/overview");
        mutateRequest("/channels");
        mutateRequest("/channel-releases");
        setCreating(false);
      },
      onError: (error: unknown) => toastError(toast, "记录发布失败", error),
    });

  // 右栏渠道包列表:该渠道的构建产物(Jenkins 回流,GCS 签名 URL 下载)
  interface ChannelArtifact {
    id: string;
    versionId: string;
    fileName: string;
    size: number;
    versionName?: string;
    versionCode?: string;
    createdAt: string;
  }
  const { data: adata } = useSWR<{ data: ChannelArtifact[]; total: number }>(
    channelId
      ? swrKey("/artifacts", { pid: id, channelId, pageSize: 8 })
      : null,
    { keepPreviousData: true }
  );
  const artifacts = adata?.data ?? [];
  // 签名 URL 短时效:点击时现取现开(Safari 对 await 后的 window.open 拦截,
  // 先开窗后回填,同 app-artifacts)
  const downloadArtifact = async (a: ChannelArtifact) => {
    const win = window.open("", "_blank");
    try {
      const res = await fetcher<{ url: string }>([
        `/versions/${a.versionId}/artifacts/${a.id}/download-url`,
      ]);
      if (win) win.location.href = res.url;
      else window.location.href = res.url;
    } catch (error) {
      win?.close();
      toastError(toast, "下载失败", error);
    }
  };

  // 删除一条记录(误录时用);带确认,只删当条,其余步保留
  const [removing, setRemoving] = useState<
    ChannelDetail["releases"][number] | null
  >(null);
  const { trigger: removeRelease, isMutating: removingRelease } =
    useSWRMutation("/channel-releases", delFetcher<{ id: number }>, {
      onSuccess: ({ id: removedId }) => {
        writeAtom<ChannelDetail>(`/channels/${channelId}`, (cur) => ({
          ...cur,
          releases: cur.releases.filter((r) => String(r.id) !== String(removedId)),
        }));
        mutateRequest("/channels/overview");
        mutateRequest("/channels/matrix");
        mutateRequest("/channel-releases");
        setRemoving(null);
      },
      onError: (error: unknown) => toastError(toast, "删除记录失败", error),
    });


  if (isLoading) return <ListLoading />;
  if (!channel) return <ListEmpty>渠道不存在。</ListEmpty>;

  // 渠道版本号只进不退(服务端同口径校验):记录过的最大 versionCode 即
  // 渠道的 last version code,下拉滤掉比它小的版本;等于允许(同版本分阶段)
  const lastCode = Math.max(
    0,
    ...channel.releases.map((r) => Number(r.version?.versionCode) || 0)
  );
  // 已在渠道上走过 flow 的版本(继续它们的 walk 不受门槛/只进不退限制),
  // 并入下拉;新版本才按 versionCode ≥ lastCode 过滤
  const onChannelVersions: VersionOption[] = [];
  {
    const seen = new Set<string>();
    for (const r of channel.releases) {
      if (r.version && !seen.has(r.versionId)) {
        seen.add(r.versionId);
        onChannelVersions.push(r.version);
      }
    }
  }
  const onChannelIds = new Set(onChannelVersions.map((v) => String(v.id)));
  const selectableVersions = [
    ...onChannelVersions,
    ...versionOptions.filter(
      (v) =>
        !onChannelIds.has(String(v.id)) &&
        (!lastCode || !v.versionCode || Number(v.versionCode) >= lastCode)
    ),
  ].sort(
    (a, b) => (Number(b.versionCode) || 0) - (Number(a.versionCode) || 0)
  );

  // 每个版本在该渠道的最新一条 = 该版本 walk 的当前位置,行尾给 Next;
  // 同版本更早的记录是历史,不再给(列表新在前,首次出现即最新)
  const latestRowIds = new Set<number>();
  {
    const seen = new Set<string>();
    for (const r of channel.releases) {
      if (!seen.has(r.versionId)) {
        seen.add(r.versionId);
        latestRowIds.add(r.id);
      }
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <PageBreadcrumb
        parent={[
          { label: app?.name ?? "App", to: `/apps/${id}/versions` },
          { label: "Channels", to: `/apps/${id}/channels` },
        ]}
        current={channel.name}
      />

      <div className="flex items-center gap-2">
        {/* Title prefers the Google Play store name; the internal codename
            stays visible beside it (and in the breadcrumb). */}
        <h1 className="text-2xl font-semibold leading-tight">
          {channel.playName || channel.name}
        </h1>
        {channel.playName && (
          <span className="text-sm text-muted-foreground">{channel.name}</span>
        )}
        <span className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => {
            setBVersionId("");
            setBMode("all");
            setBuildOpen(true);
          }}
        >
          打渠道包
        </Button>
        <ChannelFormDialog
          pid={String(id)}
          channel={channel}
          onSaved={() => mutateRequest(`/channels/${channelId}`)}
          trigger={
            <Button
              variant="ghost"
              size="icon"
              title={`编辑 ${channel.name}`}
              aria-label={`编辑 ${channel.name}`}
              className="h-6 w-6 shrink-0 text-muted-foreground"
            >
              <PencilIcon className="h-3.5 w-3.5" />
            </Button>
          }
        />
        <DeleteChannelButton
          channel={channel}
          onDeleted={() => navigate(`/apps/${id}/channels`)}
        />
      </div>

      <DetailColumns>
        <DetailMain>
          <Section
            title={`发布记录 (${channel.releases.length})`}
            action={
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 px-2 text-xs"
                onClick={() => {
                  setCVersionId("");
                  setCDate(format(new Date(), "yyyy-MM-dd"));
                  setCPercent("");
                  // 渠道无整体状态:新 walk 默认从流程入口起步
                  setCState(channelFlowStart(flowMap));
                  setCNextsOnly(null);
                  setCreating(true);
                }}
              >
                <CirclePlusIcon className="h-3.5 w-3.5" /> 记录发布
              </Button>
            }
          >
            {channel.releases.length === 0 ? (
              <ListEmpty>还没有发布记录。点右上「记录发布」。</ListEmpty>
            ) : (
              <div className="flex flex-col gap-1">
                {channel.releases.map((r, i) => (
                  <RowCard
                    key={r.id}
                    onClick={() =>
                      navigate(`/apps/${channel.pid}/versions/${r.versionId}`)
                    }
                  >
                    <RowIndex n={i + 1} />
                    <span className="min-w-0 flex-1 truncate">
                      {r.version?.versionName ?? r.versionId}
                      {r.version?.versionCode && `(${r.version.versionCode})`}
                    </span>
                    {/* 该条记录走到的 channel flow 节点(旧数据可能为空) */}
                    <span className="flex w-14 shrink-0 items-center">
                      {r.state && <ChannelStateChip state={r.state} />}
                    </span>
                    {/* 记录是不可变的步进:百分比只读,且只在放量中显示 */}
                    <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                      {r.state === "rolling" && r.percent != null
                        ? `${r.percent}%`
                        : "—"}
                    </span>
                    {r.operatorName && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {r.operatorName}
                      </span>
                    )}
                    <span className="w-24 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                      {format(new Date(r.releasedAt), "yyyy-MM-dd")}
                    </span>
                    {/* 各版本最新一条的尾部一个 Next:点开记录弹窗,
                        节点限定为该条节点的 nexts(个数不定,不平铺按钮) */}
                    {/* 固定槽位:无 Next 的行放空占位,与有按钮的行对齐 */}
                    <span className="flex w-14 shrink-0 justify-end">
                      {latestRowIds.has(r.id) && r.state && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setCVersionId(r.versionId);
                            setCDate(format(new Date(), "yyyy-MM-dd"));
                            setCPercent("");
                            setCState("");
                            // 可选项 = 当前节点自身 + 图上的 nexts:停在原
                            // 节点再走一步就是分阶段放量(如 50%→70%)
                            setCNextsOnly([
                              r.state as string,
                              ...(flowMap[r.state as string] ?? []),
                            ]);
                            setCreating(true);
                          }}
                        >
                          Next
                        </Button>
                      )}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="删除这条记录"
                      className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setRemoving(r);
                      }}
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </Button>
                  </RowCard>
                ))}
              </div>
            )}
          </Section>
        </DetailMain>

        <DetailAside>
          <Section title={`渠道包 (${adata?.total ?? 0})`}>
            {artifacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                还没有产物。点上方「打渠道包」提交构建。
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                {artifacts.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 rounded-md border px-2 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs" title={a.fileName}>
                        {a.fileName}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {a.versionName ?? a.versionId}
                        {a.versionCode && `(${a.versionCode})`} ·{" "}
                        {formatSize(a.size)} ·{" "}
                        {format(new Date(a.createdAt), "MM-dd HH:mm")}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="下载"
                      className="h-6 w-6 shrink-0 text-muted-foreground"
                      onClick={() => downloadArtifact(a)}
                    >
                      <DownloadIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Section>
          <PropertyList>
            {channel.flavorPid && (
              <Property label="Flavor pid">
                <span
                  title="versionNameFlavorPid"
                  className="tabular-nums text-muted-foreground"
                >
                  #{channel.flavorPid}
                </span>
              </Property>
            )}
            {!!channel.feedMode && (
              <Property label="feedMode">
                <Chip tone="teal">feed</Chip>
              </Property>
            )}
            {channel.playName && (
              <Property label="Play 应用名">
                <span title="Google Play 上运营的应用名">
                  {channel.playName}
                </span>
              </Property>
            )}
            {channel.packageName && (
              <Property label="包名">
                <a
                  href={playStoreUrl(channel.packageName)}
                  target="_blank"
                  rel="noreferrer"
                  title="Google Play"
                  className="inline-flex min-w-0 max-w-full items-center gap-1 font-mono text-xs text-muted-foreground hover:text-primary hover:underline"
                >
                  <span className="truncate">{channel.packageName}</span>
                  <ExternalLinkIcon className="h-3 w-3 shrink-0" />
                </a>
              </Property>
            )}
          </PropertyList>
        </DetailAside>
      </DetailColumns>

      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>记录 {channel.name} 发布</DialogTitle>
            <DialogDescription>
              版本在该渠道上走 channel flow：每次变更记一条，选择本次走到的
              节点，渠道状态随之更新；旧记录保留为历史。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">版本</span>
              <Select value={cVersionId} onValueChange={setCVersionId}>
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue placeholder="选择版本…" />
                </SelectTrigger>
                <SelectContent>
                  {selectableVersions.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      {versionOptions.length
                        ? `没有 versionCode ≥ ${lastCode} 的可发版本(不能回退)`
                        : "没有满足发版门槛的版本(见 Settings → Channel flow)"}
                    </div>
                  )}
                  {selectableVersions.map((v) => (
                    <SelectItem key={v.id} value={String(v.id)}>
                      <span className="flex items-center gap-2">
                        {v.versionName ?? v.id}
                        {v.versionCode && `(${v.versionCode})`}
                        {v.state !== undefined && <StateBadge state={v.state} />}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lastCode > 0 && (
                <p className="text-xs text-muted-foreground">
                  该渠道已发布到 versionCode {lastCode},版本号只能 ≥ 它。
                </p>
              )}
            </div>
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">流转到</span>
              <Select value={cState} onValueChange={setCState}>
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue placeholder="选择 channel flow 节点…" />
                </SelectTrigger>
                <SelectContent>
                  {(cNextsOnly ?? flowNodes).map((s) => (
                    <SelectItem key={s} value={s}>
                      <span className="flex items-center gap-2">
                        <ChannelStateChip state={s} />
                        <span className="font-mono text-xs text-muted-foreground">
                          {s}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <label className="block flex-1 space-y-1">
                <span className="text-sm text-muted-foreground">发布日期</span>
                <Input
                  type="date"
                  value={cDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCDate(e.target.value)
                  }
                  className="h-8"
                />
              </label>
              {/* 只有 rolling 带比例且必填;其余节点不记 */}
              {cState === "rolling" && (
                <label className="block w-24 space-y-1">
                  <span className="text-sm text-muted-foreground">百分比</span>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={cPercent}
                    placeholder="50"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCPercent(e.target.value)
                    }
                    className="h-8 tabular-nums"
                  />
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreating(false)}
            >
              取消
            </Button>
            <Button
              size="sm"
              disabled={
                creatingRelease ||
                !cVersionId ||
                !cDate ||
                !cState ||
                (cState === "rolling" && !cPercent.trim())
              }
              onClick={() =>
                createRelease({
                  versionId: cVersionId,
                  channelId: Number(channelId),
                  releasedAt: withCurrentTime(cDate),
                  ...(cState === "rolling"
                    ? { percent: Number(cPercent.trim()) }
                    : {}),
                  state: cState,
                })
              }
            >
              {creatingRelease ? "保存中…" : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={buildOpen} onOpenChange={(o) => !o && setBuildOpen(false)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>打 {channel.name} 渠道包</DialogTitle>
            <DialogDescription>
              选择版本提交 Jenkins 打包
              {channel.flavorPid
                ? `;flavor 固定为本渠道(#${channel.flavorPid})`
                : ""}
              。提交成功仅代表进入队列,产物回流后在版本 Artifacts 查看。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">版本</span>
              <Select value={bVersionId} onValueChange={setBVersionId}>
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue placeholder="选择版本…" />
                </SelectTrigger>
                <SelectContent>
                  {selectableVersions.map((v) => (
                    <SelectItem key={v.id} value={String(v.id)}>
                      <span className="flex items-center gap-2">
                        {v.versionName ?? v.id}
                        {v.versionCode && `(${v.versionCode})`}
                        {v.state !== undefined && <StateBadge state={v.state} />}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">模式</span>
              <Select
                value={bMode}
                onValueChange={(v: string) => setBMode(v as "apk" | "all")}
              >
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Release build(APK + AAB)</SelectItem>
                  <SelectItem value="apk">Test build(仅 APK)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {bVersionId && buildOptions && !buildOptions.supported && (
              <p className="text-xs text-destructive">该 App 不支持系统内打包。</p>
            )}
            {bVersionId &&
              buildOptions?.needFlavors &&
              !channel.flavorPid && (
                <p className="text-xs text-destructive">
                  该渠道未配置 flavorPid,无法定位渠道包,请先在编辑里补上。
                </p>
              )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBuildOpen(false)}
            >
              取消
            </Button>
            <Button
              size="sm"
              disabled={
                building ||
                !bVersionId ||
                !buildOptions?.supported ||
                (buildOptions?.needFlavors && !channel.flavorPid)
              }
              onClick={() =>
                triggerBuild({
                  mode: bMode,
                  ...(buildOptions?.needFlavors && channel.flavorPid
                    ? { flavorPids: [Number(channel.flavorPid)] }
                    : {}),
                })
              }
            >
              {building ? "提交中…" : "提交打包"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>删除这条发布记录?</AlertDialogTitle>
            <AlertDialogDescription>
              删除 {removing?.version?.versionName ?? removing?.versionId} 在{" "}
              {channel.name} 的这一步
              {removing?.state === "rolling" &&
                removing.percent != null &&
                `(${removing.percent}%)`}
              ;该 walk 的其余记录保留。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={removingRelease}
              onClick={() => removing && removeRelease(String(removing.id))}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AppChannelDetail;
